import {
  InlandMarineTypes,
  FarmRiskType,
  LiabilityType,
  StructureCategory,
  lookupsJson,
  FarmScheduleCoverageType,
  standardizedCancelNonRenewalReasonCodes
} from '@ourbranch/lookups';
import { policyTypes } from '@ourbranch/policy-types';

import farmUWQuestions from './forreston/farm-uw-questions';

const {
  deductibleAllOther: allOtherOptions,
  scheduledFarmPropertyCategories,
  agriculturalConstructionType
} = lookupsJson.farm;
const allConstructionTypeIds = agriculturalConstructionType.map((ct) => ct.id);

const dwellingInlandMarineItemTypes = [
  InlandMarineTypes.Atv,
  InlandMarineTypes.BoatDock,
  InlandMarineTypes.Boats,
  InlandMarineTypes.CoinCollection,
  InlandMarineTypes.FineArts,
  InlandMarineTypes.Furs,
  InlandMarineTypes.GolfCarts,
  InlandMarineTypes.GolfEquipment,
  InlandMarineTypes.Guns,
  InlandMarineTypes.Jewelry,
  InlandMarineTypes.LawnMowers,
  InlandMarineTypes.MiscellaneousProperty,
  InlandMarineTypes.MusicalInstruments,
  InlandMarineTypes.PersonalTools,
  InlandMarineTypes.Silverware,
  InlandMarineTypes.SportingEquipment,
  InlandMarineTypes.Tack,
  InlandMarineTypes.UnmannedAircraft
].sort((a, b) => a.id.localeCompare(b.id));

const config = {
  spinner: 'ember',
  // How long the newer-offer interstitial dwells before it redirects (#30994). It is the only
  // window staff have to reach "Keep version" and stay on the offer they opened, so Forreston doubles it to 10s — its agents asked for longer to react.
  offerRedirectInterstitialMs: 10000,
  // Executive management report (Vantage), surfaced as a "Reports" item in the account menu for
  // internal users holding the ViewReports permission. The URL is per-brand so CFM and the other
  // mutuals can opt in later by adding their own `reports.url`; brands without it show no item. (#35090)
  reports: {
    url: 'https://vantage.data.ourbranch.com/forreston/executive/management-report/#summary'
  },
  // Issue #33591: standardized cancellation / non-renewal reasons shared across the mutuals.
  // The ordered ids turn on the matching `optIn` entries from the `cancelReason` superset.
  // Issue #33913: append the legacy "Policy Rewrite" (CNRW) reason so mutuals get the same rewrite
  // flow as Branch/Bix. Selecting CNRW flat-cancels the term and surfaces the "Rewrite Policy" button
  // (isPolicyEligibleForRewrite keys off acceptableCancelCodesForRewrite = ['CNRW']). CNRW is
  // permission-gated on canRewritePolicy in cancellation-form.jsx, so only rewrite-capable users see it.
  // Issue #35987: NACP ('Policy Not Accepted') brings the mutuals to parity with Branch/BIX. It is a
  // plain (non-optIn) cancellation reason in the superset, so it resolves into the CANCEL dropdown
  // (not non-renewal) and stops a cancelled no-renew policy being treated as a fresh cancel reason.
  cancelNonRenewalReasons: [...standardizedCancelNonRenewalReasonCodes, 'CNRW', 'NACP'],
  // #36221: initial state of the "Refund credit card fee" cancellation checkbox. false = keep the
  // card-processing fee for the payments already made (the default for every mutual); set true only
  // where compliance requires the fee to be refunded. Must match the backend default in
  // packages/brand-config-be (fees.farm.refundCreditCardFeeOnCancellation).
  refundCreditCardFeeOnCancellationByDefault: false,
  featureFlags: {
    // A customer can hold a FarmHome (Z) quote on one property and a FarmRental (Y) quote on
    // another. Deduping offer search on `webUserId` hid one of them, so agents kept opening and
    // editing the wrong quote (#34790). Dedupe on `offerSearchDedupeKey` instead — still one row
    // per quote, but per product set rather than per customer.
    //
    // ON: forreston-production's offer index was reindexed on 2026-09-04 (releases
    // forreston-v2026-09-04-1 and -2) — 6,705 documents, all carrying the key, rebuild lossless.
    //
    // Forreston needed two passes. Its farm renewal offers carry no dates (so the first reindex
    // had to stop deleting them, #34802) and no webUserId (so the dedupe key had to stop
    // collapsing them, #34805 — 424 documents were sharing 3 keys). Verified on the live index
    // before enabling: the 424 webUserId-less offers now resolve to 424 distinct keys rather
    // than 3, and a real search returns more rows than before, not fewer.
    offerSearchDedupesByProductSet: true,
    // Forreston farm does not rate on the pulled insurance score (sends 0), so the underwriter
    // override is off here — it only applies to CFM and Oakwood. (#34377)
    insuranceScoreOverride: false,
    // Protection-class override IS enabled for Forreston (unlike the insurance-score override): fpc
    // is a fire-rating input on Forreston farm property, not a credit tier. (#35212)
    protectionClassOverride: true,
    // Descope-authority override that LOCKS Coverage A & Coverage E limits with a required internal
    // reason (#35617). On for the mutual brands; off for Branch. See branch.js for the full note.
    coverageLimitOverride: true,
    // Agency users never see the applicant's insurance score. `hiddenFields` below currently hides
    // it from everyone on Forreston, so this flag changes nothing today — it is what keeps the
    // score away from agents on the day Forreston drops that entry. (#35046)
    hideInsuranceScoreFromAgents: true,
    phoneRequired: true,
    applicantDateOfBirthAtNewOffer: true,
    // Letter of Experience generator is Branch-only for now (#29700) — off until the mutual
    // letter format is confirmed.
    generateLoe: false,
    // Underwriters (CanDeleteInternalDocuments) may delete offer Supporting Documents; mutual-only,
    // off on Branch. (#34202)
    deleteSupportingDocuments: true,
    // Staff "Send Invoice" billing action; mutual-only, off on Branch. (#34203, #34419)
    sendInvoice: true,
    shouldAutoExcludeCosmeticDamage: true,
    // Forreston farm agents may have a personal mailing address in a state they don't write
    // in; the licensed-state check is enforced per farm location instead. (Issue #30876)
    enforceLicensedStateOnNamedInsured: false,
    // Show the agency Portal (Notion pages) to every user on this mutual, not just
    // agents/underwriters. (#31094, #33024)
    agencyPortalVisibleToAllUsers: true,
    // Display/edit the policy EXPIRATION date instead of the stored endDate ("Last Full Day Of
    // Coverage"). Matches the legacy IMT presentation; the stored endDate is unchanged. Must live on
    // the TOP-LEVEL featureFlags — showExpirationInsteadOfEndDate() reads it from there, not from the
    // farm/state-scoped block. (#32070, #32729)
    showExpirationDateInsteadOfEndDate: true,
    // Agency sweep accounts (#33155): independent agents can pay from their agency's
    // Stripe account. On in non-prod and now in production too (#35085).
    sweepAccounts: { enabledInNonProd: true, enabledInProd: true },
    // Agency Reports (#36219): Branch-only until this brand has its own report bucket.
    agencyReports: { enabled: false, visibleToAgencies: false },
    // Underwriter MicroBilt-bypass on ACH adds (#33938): mutual-only, on in every env.
    microBiltVerificationBypass: true,
    // "Collect payment information later" at bind (#33891): mutual-only.
    collectPaymentLater: true,
    // Policy drafting (working drafts / draft-and-keep-changing endorsement flow).
    // Consumed via core/helpers/policy-drafting.js.
    policyDrafts: true
  },
  draftablePolicyTypes: [policyTypes.Farm, policyTypes.FarmHome, policyTypes.FarmRental],
  // Forreston effective date rules by role and transaction type (GitHub issue #29728)
  effectiveDateRules: {
    agent: {
      newBusiness: { maxBackdateDays: 5, maxForwardDays: 60 },
      endorsement: { maxBackdateDays: 30, maxForwardDays: 60 }
    }
    // Underwriters (internal users): no restrictions — handled by omission
  },
  // Forreston cannot write farm policies effective before this date (backend source of truth
  // lives in packages/brand-config-be/src/brands/forreston.ts; keep these in sync).
  farmMinimumEffectiveDate: '2026-05-01',
  hiddenFields: [
    'applicant.insuranceScore',
    'applicant.gender',
    'applicant.maritalStatus',
    'applicant.highestEducation'
  ],
  helpWidget: {
    showPhone: true,
    // #35911: the Staff "?" speed dial links agents to the Forreston FNOL form to submit a claim.
    fnolUrl: 'https://forreston.ember.liberate.claims/fnol'
  },
  discounts: {
    hasFutureEffectiveDateDiscount: false,
    hasAdvanceQuoteDiscount: false,
    hasPaperlessDiscount: false,
    hasAutoHomeBundleDiscount: true
  },
  farm: {
    // Forreston farm UW questions (question text, endorsement subset, policy-type-aware sections).
    // Sourced from ./forreston/farm-uw-questions.js so each brand owns its own question tree.
    uwQuestions: farmUWQuestions,
    IL: {
      coverageLimits: {
        // Mine Subsidence coverage limit for all farm structures (GitHub issue #28634)
        mineSubsidenceCoverageLimit: 750000,
        // Minimum Coverage A floor for dwelling risks (GitHub issue #28995).
        // #34442: this is Clarion Door's minimum rateable Coverage A ($2,000); a dwelling floored
        // below it rates to "unable to locate factors" and a $0 segment premium. Mirrors the BE
        // brand config (packages/brand-config-be/src/brands/forreston.ts).
        minimumDwellingCoverageA: 2000,
        // Base pool limit for above-ground pools (GitHub issue #29233)
        aboveGroundPoolDefaultLimit: 2500
      },
      featureFlags: {
        // Forreston does not offer unscheduled personal property on any risk type (GitHub issue #28933)
        hideUnscheduledPersonalProperty: true,
        // Offer Palomar earthquake coverage on the primary dwelling risk (GitHub issue #33499).
        // Read by EarthquakePalomarCoverageForm; farm[state].featureFlags block (NOT top-level).
        palomarEQOffered: true
      },
      liabilityExtensions: {
        rented: {
          [LiabilityType.Personal]: { locations: true, residences: true, structures: true },
          [LiabilityType.Commercial]: { locations: true, residences: true, structures: true }
        },
        occupied: {
          [LiabilityType.Personal]: { locations: true, residences: true },
          [LiabilityType.Commercial]: { locations: false, residences: false }
        },
        assets: {
          [LiabilityType.Personal]: { atv: true, golfCart: true, unmannedAircraft: true, boat: true },
          [LiabilityType.Commercial]: { atv: true, golfCart: true, unmannedAircraft: true, boat: false }
        }
      },
      /**
       * Policy type masks define how Home (Z) and Rental (Y) differ from full Farm (F).
       * Each mask is a set of overrides — components read from the mask and fall back to default Farm behavior
       * when a value is null/undefined. F's mask is intentionally permissive (no restrictions).
       *
       * SOURCE OF TRUTH: The canonical mask config lives in @ourbranch/brand-config-be (forreston.ts).
       * This staff-FE copy must stay in sync. The backend only enforces coverageBRule (pricing).
       * All other mask fields are UI-only — the backend stores full farm data regardless of type.
       */
      policyTypeMasks: {
        // Full Farm — permissive mask, no restrictions over default behavior
        F: {
          label: 'Farm',
          tabLabel: 'Farm Details',
          addRiskLabel: 'Add a dwelling or structure',
          interestedPartyLabel: 'Additional Interested Party',
          // #34528: Vacant-Rental ('VR') is a Rental-only FO1 usage. The F mask otherwise has no
          // residenceUsageOverrides, so it would fall through to the full base list and surface 'VR'
          // on a Farm dwelling — allow-list the Farm FO1 usages explicitly to keep it Rental-only.
          residenceUsageOverrides: {
            [FarmRiskType.Fo1]: ['P', 'S', 'R', 'V']
          }
        },
        // Home — subset of Farm for owner-occupied / seasonal / vacant homeowner properties
        Z: {
          label: 'Home',
          tabLabel: 'Home Details',
          addRiskLabel: 'Add a structure',
          interestedPartyLabel: 'Additional Insured/Interest',
          // Allowed risk types: FO1, FO2, FO3, FO4, FO6Structures only (no FO6Scheduled, FO6Unscheduled)
          allowedRiskTypes: [
            FarmRiskType.Fo1,
            FarmRiskType.Fo2,
            FarmRiskType.Fo3,
            FarmRiskType.Fo4,
            FarmRiskType.Fo6Structures
          ],
          // Residence usage overrides by risk type — remove Rental ('R') from dwelling types
          residenceUsageOverrides: {
            [FarmRiskType.Fo1]: ['P', 'S', 'V'],
            [FarmRiskType.Fo2]: ['P', 'S'],
            [FarmRiskType.Fo3]: ['P', 'S']
          },
          // FO6Structures: barns/outbuildings, fencing, and dwelling & contents. Dwelling &
          // Contents is the only way to add a seasonal dwelling alongside the primary
          // dwelling on a Home policy (GitHub issue #31620).
          allowedFO6StructureCategories: [
            StructureCategory.BarnsOutbuildings,
            StructureCategory.Fencing,
            StructureCategory.DwellingContents
          ],
          // Barns/Outbuildings "Occupancy or use type" is restricted to a single "General Use"
          // option on Home — the farm-specific uses (Calf Barn, Dairy Barn, Machine Shed, etc.)
          // don't apply to a homeowner structure. Id stays 'LVB' so rating is unchanged (#32485).
          occupancyUseTypeOverrides: {
            [StructureCategory.BarnsOutbuildings]: [{ id: 'LVB', value: 'General Use' }]
          },
          fo6StructuresLabel: 'Other Structures (FO-6)',
          // Max one dwelling risk (FO1/FO2/FO3) per policy
          maxDwellingRisks: 1,
          // Liability: personal and commercial allowed
          allowedLiabilityTypes: [LiabilityType.Personal, LiabilityType.Commercial],
          // Home (Z) shows premises-only liability, not "Commercial" — a homeowner product, not a
          // farm operation. Matches Rental (Y); full Farm (F) keeps the "Commercial Liability (Farm
          // Premises and Operations)" lookups default. Rating id ('C') is unchanged. (#31695, #33064)
          liabilityTypeLabels: {
            [LiabilityType.Personal]: 'Personal Liability Coverage',
            [LiabilityType.Commercial]: 'Premises Liability Coverage'
          },
          // Liability section: hide "Primary farm operations" (defaulted to 'O' behind the
          // scenes for the rater) and rename "Farm details" (GitHub issue #31697)
          hideFarmOperations: true,
          liabilityDetailsLabel: 'Home details',
          // Hide farm-specific liability coverages per liability type
          hiddenLiabilityCoverages: {
            [LiabilityType.Personal]: ['farmPollution', 'employersLiability', 'customFarming'],
            [LiabilityType.Commercial]: [
              'farmPollution',
              'employersLiability',
              'customFarming',
              'customFeeding',
              'incidentalLiability'
            ]
          },
          // GL-4002 (lessor of leased equipment) is not offered on Home, regardless of
          // liability type (#32165).
          hiddenInterestedPartyRelationships: {
            [LiabilityType.Personal]: ['lessorOfLeasedEquipment'],
            [LiabilityType.Commercial]: ['lessorOfLeasedEquipment']
          },
          // No inland marine type restrictions for home — uses same as dwelling default
          hiddenInlandMarineTypes: null,
          // Hide farm extra expense endorsement
          hiddenEndorsements: ['farmExtraExpense'],
          // Hard block: acreage must be <= 40
          maxAcres: 40,
          // Address toggle remains enabled
          disableAddressToggle: false,
          // Coverage B locked at 10% of Coverage A for dwelling risks
          coverageBRule: { lockedPercent: 0.1 },
          // Coverage B is titled "Related Private Structures (B)" on Home per the Forreston forms,
          // not the default "Other Structures (B)" (#32465).
          coverageBLabel: 'Related Private Structures (B)',
          // Liability "rented to others" extension: a homeowner only rents out additional
          // residences, not farm premises or structures, so drop the farm-worded location/
          // structure options and keep only "Additional residences rented to others" (#32255)
          allowedRentedExtensionTypes: ['residences'],
          // Loss Payee interested parties don't schedule farm machinery on Home, so its
          // "Associated equipment" field is dropped (Additional Interest keeps it). (#32521)
          hideLossPayeeEquipment: true
        },
        // Rental — subset of Farm for tenant-occupied rental dwelling properties
        Y: {
          label: 'Rental',
          tabLabel: 'Dwelling Details',
          addRiskLabel: 'Add a structure',
          interestedPartyLabel: 'Additional Insured/Interest',
          // Allowed risk types: FO1, FO2, FO3, FO6Structures only (no FO4, FO6Scheduled, FO6Unscheduled)
          allowedRiskTypes: [FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3, FarmRiskType.Fo6Structures],
          // Residence usage overrides by risk type — Tenant-Occupied ('R'), plus Vacant-Rental
          // ('VR') on FO1 only (#34528)
          residenceUsageOverrides: {
            [FarmRiskType.Fo1]: ['R', 'VR'],
            [FarmRiskType.Fo2]: ['R'],
            [FarmRiskType.Fo3]: ['R']
          },
          // FO6Structures limited to barns/outbuildings and fencing
          allowedFO6StructureCategories: [StructureCategory.BarnsOutbuildings, StructureCategory.Fencing],
          // Barns/Outbuildings "Occupancy or use type" is restricted to a single "General Use"
          // option on Rental — the farm-specific uses (Calf Barn, Dairy Barn, Machine Shed, etc.)
          // don't apply to a rental structure. Id stays 'LVB' so rating is unchanged (#32485).
          occupancyUseTypeOverrides: {
            [StructureCategory.BarnsOutbuildings]: [{ id: 'LVB', value: 'General Use' }]
          },
          fo6StructuresLabel: 'Other Structures (FO-6)',
          // No dwelling limit for rental
          maxDwellingRisks: null,
          // Liability: premises-only. Rental keeps the premises-liability RATING (id 'C' → the
          // Clarion Door premises chart, insured_resides_in_dwellingYN=0 for a landlord), so the id
          // stays Commercial and rating/forms are unchanged. #33009: Mutual Services flagged that
          // labeling it "Commercial Liability" is wrong for a landlord product — the label below
          // drops "Commercial" (and the earlier farm references from #31695) and reads as plain
          // premises liability.
          allowedLiabilityTypes: [LiabilityType.Commercial],
          liabilityTypeLabels: {
            [LiabilityType.Commercial]: 'Premises Liability Coverage'
          },
          // Liability section: hide "Primary farm operations" (defaulted to 'O' behind the
          // scenes for the rater) and rename "Farm details" (GitHub issue #31697)
          hideFarmOperations: true,
          liabilityDetailsLabel: 'Dwelling details',
          // Hide farm-specific liability coverages for commercial
          hiddenLiabilityCoverages: {
            [LiabilityType.Commercial]: [
              'farmPollution',
              'employersLiability',
              'customFarming',
              'customFeeding',
              'incidentalLiability',
              // The "Incidental liability (ATVs, golf carts, unmanned aircraft)" assets section is
              // not eligible on rentals (#32042). Distinct from incidentalLiability above, which
              // gates the businessPursuits coverage.
              'additionalAssetsExtensions'
            ]
          },
          // Additional Insured/Interest relationship dropdown removals for rentals:
          //  - GL-4002 / lessorOfLeasedEquipment — not offered on rentals (#32043)
          //  - GL-3001 / managerOrLessorOfPremisesCommercial, FO-41 / propertyCoverages, and
          //    Landlord (proof of coverage) — do not apply to rentals (#32356)
          hiddenInterestedPartyRelationships: {
            [LiabilityType.Commercial]: [
              'lessorOfLeasedEquipment',
              'managerOrLessorOfPremisesCommercial',
              'propertyCoverages',
              'landlord'
            ]
          },
          // FO-41 (Additional Insureds - Property coverages) does not apply to rentals, so the
          // FO-41 add-on toggle is removed from the Additional Insured/Interest section (#32359).
          hideFO41: true,
          // The "Specific activity or location exclusion" toggle is not needed for rentals (#32361).
          hideLiabilityExclusions: true,
          // Remove most inland marine types — only keep computer, tools, misc equipment, lawn mowers,
          // plus Boat Dock ($2/$1000, rated in Clarion Door per Rental FMIC DW 20 — #32075)
          hiddenInlandMarineTypes: [
            'atv',
            'boat',
            'coinCollection',
            'fineArts',
            'furs',
            'golfCart',
            'golfEquipment',
            'guns',
            'jewelry',
            'musicalInstruments',
            'silverware',
            'sportingEquipment',
            'tack',
            'unmannedAircraft'
          ],
          // Hide farm extra expense and identity fraud endorsements
          hiddenEndorsements: ['farmExtraExpense', 'identityFraud'],
          // Hard block: acreage must be <= 40
          maxAcres: 40,
          // Address toggle disabled — all locations use primary address
          disableAddressToggle: true,
          // Coverage B locked to 10% of Coverage A, recomputed on every rate — same as Home (#32030).
          // (The earlier editable `defaultPercent` variant went stale when Coverage A changed.)
          coverageBRule: { lockedPercent: 0.1 },
          // Coverage B is titled "Related Private Structures (B)" on Rental per the Forreston forms,
          // not the default "Other Structures (B)" (#32465).
          coverageBLabel: 'Related Private Structures (B)',
          // Coverage D is titled "Loss of Rent (D)" on Rental (a rental has no additional-living-cost
          // exposure), with a loss-of-rent "more info" tooltip, not the default "Addit. Living Cost (D)"
          // (#32458).
          coverageDLabel: 'Loss of Rent (D)',
          coverageDTooltip:
            'Reimburses lost rental income if the dwelling becomes uninhabitable due to a covered loss, up to the amount entered here.',
          // "Expanded replacement cost" does not apply to Rental and errors in Clarion Door, so it is
          // hidden from the Rental base coverages (#32455).
          hideExpandedReplacementCost: true,
          // Liability "rented to others" extension: like Home, a rental only rents out additional
          // residences, not farm premises or structures — keep only "Additional residences rented
          // to others". Forreston allows more than one rental per policy, so this is a real need;
          // it restores the residences-only option that #32362 had (incorrectly) removed. (#34366)
          allowedRentedExtensionTypes: ['residences'],
          // Loss Payee interested parties don't schedule farm machinery on Rental, so its
          // "Associated equipment" field is dropped (Additional Interest keeps it). (#32525)
          hideLossPayeeEquipment: true
        }
      },
      risks: {
        [FarmRiskType.Fo1]: {
          hiddenCoverages: ['earthquakeCoverage'],
          customFormComponentPaths: {
            cosmeticDamageExclusionSection: 'base/cosmetic-damage-exclusion-section',
            deductibleAllOther: 'base/deductible-all-other',
            deductibleWindHail: 'base/deductible-wind-hail',
            roofLossSettlement: 'base/roof-loss-settlement',
            repairRebuildRequirement: 'base/repair-rebuild-requirement',
            coverageMS: 'base/coverage-ms',
            coverageB: 'base/coverage-b',
            lossSettlement: 'base/loss-settlement',
            coverageD: 'FO1/coverage-d',
            replCostMobileHome: 'base/repl-cost-mobile-home'
          },
          allowedOptions: {
            inlandMarineItemTypes: dwellingInlandMarineItemTypes,
            inlandMarineDeductible: allOtherOptions.filter((option) =>
              [250, 500, 1000, 1500, 2500, 5000, 10000].includes(option.id)
            )
          },
          /**
           * Business rules for Forreston FO1:
           *
           * Coverage A:
           * - FO1 always uses ACV loss settlement
           * - Home/Modular Home (FD)/Barndominium: 50-100% of ACV
           * - Mobile Home: $7/sq ft minimum (based on sqFt), max 100% ACV
           *
           * Coverage C (Personal Property):
           * - Primary Residence: $0 - Coverage A limit (max), ACV
           * - Seasonal: $0 - Coverage A limit (max), ACV
           * - Tenant Occupied Rental: $0 - $25,000 (max), ACV
           * - Vacant: $0 - Coverage A limit (max), ACV
           *
           * Coverage D (Additional Living Cost):
           * - Primary Residence: 20% of Coverage A, locked (not editable)
           * - Seasonal: 20% of Coverage A, locked (not editable)
           * - Tenant Occupied Rental: 20% of Coverage A, locked (not editable)
           * - Vacant: $0 only (not editable)
           *
           * Related issues: #28616, #28614, #29855
           */
          businessRules: {
            coverageA: {
              // For Home, Modular Home, Barndominium: 50-100% ACV
              acvBasedStructures: {
                propertyTypes: ['H', 'FD', 'B'], // Home, Modular Home (FD), Barndominium
                minPercent: 0.5, // 50% ACV minimum
                maxPercent: 1.0 // 100% ACV maximum
              },
              // For Mobile Home: $7/sqft minimum, max ACV
              mobileHome: {
                propertyTypes: ['MH'],
                minPerSqFt: 7,
                maxPercent: 1.0 // 100% ACV maximum
              }
            },
            coverageC: {
              byResidenceUsage: {
                P: { min: 0, max: 'coverageA' }, // Primary Residence
                S: { min: 0, max: 'coverageA' }, // Seasonal
                R: { min: 0, max: 25000 }, // Tenant Occupied Rental
                V: { min: 0, max: 'coverageA' }, // Vacant
                VR: { min: 0, max: 'coverageA' } // Vacant-Rental — same as Vacant (#34528)
              }
            },
            coverageD: {
              byResidenceUsage: {
                P: { min: 0, max: 0, allowEditing: false, percentOfCoverageA: 0.2 }, // Primary Residence - 20% of CovA
                S: { min: 0, max: 0, allowEditing: false, percentOfCoverageA: 0.2 }, // Seasonal - 20% of CovA
                R: { min: 0, max: 0, allowEditing: false, percentOfCoverageA: 0.1 }, // Tenant Occupied Rental - 10% of Coverage A, locked (#32082)
                V: { min: 0, max: 0, allowEditing: false }, // Vacant - must be $0, not editable
                VR: { min: 0, max: 0, allowEditing: false } // Vacant-Rental — no Loss of Rent, $0 like Vacant (#34528)
              }
            }
          }
        },
        [FarmRiskType.Fo2]: {
          // Inland Flood (#32440) is currently enabled only for CFM MO; hidden here so the shared
          // FO2 render doesn't surface it for Forreston.
          hiddenCoverages: ['earthquakeCoverage', 'inlandFlood'],
          customFormComponentPaths: {
            cosmeticDamageExclusionSection: 'base/cosmetic-damage-exclusion-section',
            deductibleAllOther: 'base/deductible-all-other',
            deductibleWindHail: 'base/deductible-wind-hail',
            coverageB: 'base/coverage-b',
            repairRebuildRequirement: 'base/repair-rebuild-requirement',
            coverageMS: 'base/coverage-ms',
            roofLossSettlement: 'base/roof-loss-settlement',
            coverageD: 'FO2/coverage-d',
            replCostMobileHome: 'base/repl-cost-mobile-home'
          },
          allowedOptions: {
            inlandMarineItemTypes: dwellingInlandMarineItemTypes,
            inlandMarineDeductible: allOtherOptions.filter((option) =>
              [250, 500, 1000, 1500, 2500, 5000, 10000].includes(option.id)
            )
          },
          /**
           * Business rules for Forreston FO2:
           *
           * Coverage A:
           * - When ACV loss settlement is selected: Same rules as FO1
           * - When RC loss settlement is selected: Keep existing 80-100% RCE rules
           * - Home/Modular Home (FD)/Barndominium (ACV): 50-100% of ACV
           * - Mobile Home (ACV): $7/sq ft minimum (based on sqFt), max 100% ACV
           *
           * Coverage C (Personal Property) - varies by loss settlement:
           * ACV:
           *   - Primary: $0 to Coverage A (default 50% of Coverage A)
           *   - Seasonal: $0 to Coverage A (default 50% of Coverage A)
           *   - Tenant: $5,000 to $25,000
           *   - Vacant: $0 only
           * RC:
           *   - Primary: 50% Coverage A to Coverage A
           *   - Seasonal: 50% Coverage A to Coverage A
           *   - Tenant: $5,000 to $25,000
           *   - Vacant: $0 only
           *
           * Coverage D business rules for Forreston FO2 (GitHub issue #29013, #29855, #30548):
           * - ACV + Primary/Seasonal: 20% of Coverage A, locked (not editable)
           * - ACV + Tenant: 10% of Coverage A, locked (not editable)
           * - ACV + Vacant: $0 locked
           * - RC + Primary/Seasonal: 20% of Coverage A, locked (not editable)
           * - RC + Tenant: 10% of Coverage A, locked (not editable)
           * - RC + Vacant: $0 locked
           *
           * Related issues: #28616, #29011, #29013, #29855, #30548
           */
          businessRules: {
            coverageA: {
              acvBasedStructures: {
                propertyTypes: ['H', 'FD', 'B'],
                minPercent: 0.5,
                maxPercent: 1.0
              },
              mobileHome: {
                propertyTypes: ['MH'],
                minPerSqFt: 7,
                maxPercent: 1.0
              },
              rceBasedStructures: {
                minPercent: 0.8, // 80% of RCE minimum (post-1950)
                maxPercent: 1.0, // 100% of RCE maximum
                yearBuiltOverrides: {
                  threshold: 1950, // Homes built in 1950 or before
                  minPercentAtOrBefore: 0.5 // 50% of RCE minimum for older homes
                }
              }
            },
            coverageC: {
              byLossSettlement: {
                ACV: {
                  byResidenceUsage: {
                    P: { min: 0, max: 'coverageA' }, // Primary: $0 to Coverage A
                    S: { min: 0, max: 'coverageA' }, // Seasonal: $0 to Coverage A
                    R: { min: 0, max: 25000 }, // Tenant: $0 to $25K (#32041 — FO2 ACV defaults to $0)
                    V: { min: 0, max: 0 } // Vacant: $0 only
                  }
                },
                RC: {
                  byResidenceUsage: {
                    P: { minPctOfCoverageA: 0.5, max: 'coverageA' }, // Primary: 50% CovA to CovA
                    S: { minPctOfCoverageA: 0.5, max: 'coverageA' }, // Seasonal: 50% CovA to CovA
                    R: { min: 5000, max: 25000 }, // Tenant: $5K to $25K
                    V: { min: 0, max: 0 } // Vacant: $0 only
                  }
                }
              }
            },
            coverageD: {
              byLossSettlement: {
                ACV: {
                  byResidenceUsage: {
                    P: { min: 0, max: 0, allowEditing: false, percentOfCoverageA: 0.2 },
                    S: { min: 0, max: 0, allowEditing: false, percentOfCoverageA: 0.2 },
                    R: { min: 0, max: 0, allowEditing: false, percentOfCoverageA: 0.1 }, // Tenant - 10% of Coverage A, locked (#32082)
                    V: { min: 0, max: 0, allowEditing: false }
                  }
                },
                RC: {
                  byResidenceUsage: {
                    P: { min: 0, max: 0, allowEditing: false, percentOfCoverageA: 0.2 },
                    S: { min: 0, max: 0, allowEditing: false, percentOfCoverageA: 0.2 },
                    R: { min: 0, max: 0, allowEditing: false, percentOfCoverageA: 0.1 }, // Tenant - 10% of Coverage A, locked (#32082)
                    V: { min: 0, max: 0, allowEditing: false }
                  }
                }
              }
            }
          }
        },
        [FarmRiskType.Fo3]: {
          hiddenCoverages: [
            'earthquakeCoverage',
            'expandedDebrisRemoval',
            'businessProperty',
            'sprinklerLeakage',
            'inlandFlood',
            'windHailExclusion'
          ],
          allowedOptions: {
            constructionTypeExemptPropertyTypes: ['MH'],
            inlandMarineItemTypes: dwellingInlandMarineItemTypes,
            inlandMarineDeductible: allOtherOptions.filter((option) =>
              [250, 500, 1000, 1500, 2500, 5000, 10000].includes(option.id)
            )
          },
          customFormComponentPaths: {
            deductibleAllOther: 'base/deductible-all-other',
            deductibleWindHail: 'base/deductible-wind-hail',
            roofLossSettlement: 'base/roof-loss-settlement',
            repairRebuildRequirement: 'base/repair-rebuild-requirement',
            coverageB: 'base/coverage-b',
            coverageMS: 'base/coverage-ms',
            coverageD: 'FO3/coverage-d',
            cosmeticDamageExclusionSection: 'base/cosmetic-damage-exclusion-section',
            repairRebuildRequirementCalled: 'FO3/repair-rebuild-requirement',
            personalPropertyReplCost: 'FO3/personal-property-repl-cost',
            lossSettlement: 'base/loss-settlement',
            replCostMobileHome: 'base/repl-cost-mobile-home'
          },
          /**
           * Business rules for Forreston FO3:
           *
           * Coverage A:
           * - FO-3 dwelling risks: Coverage A must be 90-100% of RCE (min $1k)
           *
           * Coverage C (Personal Property) - FO3 is always RC:
           * - Primary: 75% Coverage A to Coverage A
           * - Seasonal: 75% Coverage A to Coverage A
           * - Tenant: $5,000 to $25,000
           * - Vacant: $0 only
           *
           * Coverage D business rules for Forreston FO3 (GitHub issue #29013, #30548):
           * - FO3 is always RC loss settlement
           * - Primary/Seasonal: 20% of Coverage A, locked (not editable)
           * - Tenant: 10% of Coverage A, locked (not editable)
           * - Vacant: $0 locked
           *
           * Related issues: #28684, #29011, #29013, #30548
           */
          businessRules: {
            coverageA: {
              rceBasedStructures: {
                minPercent: 0.9, // 90% of RCE minimum
                maxPercent: 1.0 // 100% of RCE maximum
              }
            },
            coverageC: {
              byResidenceUsage: {
                P: { minPctOfCoverageA: 0.75, max: 'coverageA' }, // Primary: 75% CovA to CovA
                S: { minPctOfCoverageA: 0.75, max: 'coverageA' }, // Seasonal: 75% CovA to CovA
                R: { min: 5000, max: 25000 }, // Tenant: $5K to $25K
                V: { min: 0, max: 0 } // Vacant: $0 only
              }
            },
            coverageD: {
              byResidenceUsage: {
                P: { min: 0, max: 0, allowEditing: false, percentOfCoverageA: 0.2 },
                S: { min: 0, max: 0, allowEditing: false, percentOfCoverageA: 0.2 },
                R: { min: 0, max: 0, allowEditing: false, percentOfCoverageA: 0.1 }, // Tenant - 10% of Coverage A, locked (#32082)
                V: { min: 0, max: 0, allowEditing: false }
              }
            }
          }
        },
        [FarmRiskType.Fo4]: {
          hiddenCoverages: [
            'deductibleEarthquake',
            'earthquakeCoverage',
            'expandedDebrisRemoval',
            'mineSubsidenceCoverage',
            'inlandFlood'
          ],
          allowedOptions: {
            inlandMarineItemTypes: dwellingInlandMarineItemTypes,
            inlandMarineDeductible: allOtherOptions.filter((option) =>
              [250, 500, 1000, 1500, 2500, 5000, 10000].includes(option.id)
            )
          },
          customFormComponentPaths: {
            deductibleAllOther: 'base/deductible-all-other',
            deductibleWindHail: 'base/deductible-wind-hail',
            replCostMobileHome: 'base/repl-cost-mobile-home'
          }
        },
        [FarmRiskType.Fo6Scheduled]: {
          hiddenCoverages: [
            'earthquakeCoverage',
            'deductibleEarthquake',
            'expandedDebrisRemoval',
            'hasSnowWeightCoverage'
          ],
          coverageF: {
            // #34459: "Rented or borrowed equipment" (12) is offered so it works the same way it does
            // for CFM Farm when scheduled. Like CFM, item 12 uses the generic item form (no
            // itemOverrides entry below) — it inherits Forreston's own deductible rules. "Personal
            // property" (10), "Poultry" (11), "Turkeys" (13), and "Chemicals, Seed and Miscellaneous"
            // (14) stay excluded for Forreston.
            itemTypes: scheduledFarmPropertyCategories.filter(
              (option) => !['10', '11', '13', '14'].includes(option.id)
            ),
            deductibleOptions: [
              { id: 1500, value: '$1,500' },
              { id: 2500, value: '$2,500' },
              { id: 5000, value: '$5,000' },
              { id: 10000, value: '$10,000' },
              { id: 20000, value: '$20,000' }
            ],
            livestockDeductibleOptions: [
              { id: 500, value: '$500' },
              { id: 1000, value: '$1,000' },
              { id: 1500, value: '$1,500' },
              { id: 2500, value: '$2,500' },
              { id: 5000, value: '$5,000' },
              { id: 10000, value: '$10,000' },
              { id: 20000, value: '$20,000' }
            ],
            defaultDeductible: 1500,
            defaultLivestockDeductible: 500,
            showWindHailDeductible: true,
            itemOverrides: {
              1: {
                descriptionLabel: 'Description (Make/Model)',
                showYear: true,
                serialNumberRequired: false,
                hideReplacementCost: true,
                openPerilsAlwaysOn: true,
                hideMakeField: true
              },
              2: {
                descriptionLabel: 'Description (Make/Model)',
                showYear: true,
                serialNumberRequired: false,
                openPerilsAlwaysOn: true,
                hideMakeField: true
              },
              3: {
                descriptionLabel: 'Description (Make/Model)',
                showYear: true,
                serialNumberRequired: false,
                hideReplacementCost: true,
                openPerilsAlwaysOn: true,
                hideMakeField: true
              },
              4: { descriptionLabel: 'Description' },
              5: { descriptionLabel: 'Description', hideIncreaseStackLimit: true },
              6: {
                descriptionLabel: 'Description (Make/Model)',
                hideReplacementCost: true,
                hideOpenPerils: true,
                hideMakeField: true
              },
              7: {
                descriptionLabel: 'Description',
                winterPerilsAlwaysOn: true,
                hideSpecialCattleEndorsement: true,
                hideSuffocationOfLivestock: true,
                hideAdditionalPerils: true
              },
              8: { descriptionLabel: 'Description', hideMakeField: true, hideSerialNumber: true },
              9: { descriptionLabel: 'Description', hideMakeField: true }
            }
          },
          allowedOptions: {
            peakSeasonItemTypes: scheduledFarmPropertyCategories.filter((option) =>
              ['4', '5', '14'].includes(option.id)
            ),
            scheduledCoverageTypes: [
              FarmScheduleCoverageType.SPP,
              FarmScheduleCoverageType.PEAK,
              FarmScheduleCoverageType.INCOME
            ],
            inlandMarineItemTypes: dwellingInlandMarineItemTypes,
            inlandMarineDeductible: allOtherOptions.filter((option) =>
              [250, 500, 1000, 1500, 2500, 5000, 10000].includes(option.id)
            )
          },
          customFormComponentPaths: {}
        },
        [FarmRiskType.Fo6Unscheduled]: {
          hiddenCoverages: ['earthquakeCoverage', 'deductibleEarthquake', 'hasSnowWeightCoverage'],
          allowedOptions: {
            scheduledCoverageTypes: [FarmScheduleCoverageType.SPP],
            inlandMarineItemTypes: dwellingInlandMarineItemTypes,
            inlandMarineDeductible: allOtherOptions.filter((option) =>
              [250, 500, 1000, 1500, 2500, 5000, 10000].includes(option.id)
            )
          },
          customFormComponentPaths: {
            deductibleWindHail: 'FO6Unscheduled/deductible-wind-hail'
          }
        },
        [FarmRiskType.Fo6Structures]: {
          hiddenCoverages: ['earthquakeCoverage', 'deductibleEarthquake', 'hasEarthquakeExteriorVeneerCoverage'],
          allowedOptions: {
            constructionTypes: allConstructionTypeIds.filter((id) => id !== 'MH'),
            scheduledCoverageTypes: [
              FarmScheduleCoverageType.SPP,
              FarmScheduleCoverageType.INCOME
              // Note: RELATED_PRIVATE_STRUCTURES intentionally excluded
            ],
            inlandMarineItemTypes: dwellingInlandMarineItemTypes,
            inlandMarineDeductible: allOtherOptions.filter((option) =>
              [250, 500, 1000, 1500, 2500, 5000, 10000].includes(option.id)
            )
          },
          /**
           * Business rules for Forreston FO6 Structures:
           * - Building class 1 max age: 25 years
           * - Confinement building RC eligibility max age: 15 years
           * - Coverage E: RC buildings 80-100% of RCE; ACV buildings 80% of ACV minimum, up to 100% RCE max
           *
           * Related issues: #28684, #28945, #28950, #28923
           */
          businessRules: {
            buildingClass1MaxAgeYears: 25,
            confinementBuildingMaxAgeYears: 15,
            poultryBuildingMaxAgeYears: 15,
            coverageE: {
              rceBasedStructures: {
                minPercent: 0.8, // 80% of RCE minimum
                maxPercent: 1.0 // 100% of RCE maximum
              },
              acvBasedStructures: {
                minPercent: 0.8 // 80% of ACV minimum; max is 100% RCE
              }
            }
          },
          customFormComponentPaths: {
            coverageMS: 'base/coverage-ms',
            deductibleAllOther: 'base/deductible-all-other',
            deductibleWindHail: 'base/deductible-wind-hail',
            repairRebuildRequirement: 'base/repair-rebuild-requirement',
            roofLossSettlement: 'base/roof-loss-settlement'
          }
        }
      }
    }
  },
  agencyPortalRoutes: [
    {
      route: 'updates',
      src: 'https://portal.ourbranch.com/ebd/2e8113b696aa8075800ae8df2d053e85',
      title: 'News and Updates',
      icon: 'portal'
    },
    {
      route: 'reports',
      src: 'https://portal.ourbranch.com/ebd/2e8113b696aa80189f37c2479e6755f6',
      title: 'Reports & Commissions',
      icon: 'pieChart'
    },
    {
      route: 'underwriting',
      src: 'https://portal.ourbranch.com/ebd/2e8113b696aa80ce8c83de8ad707c4c2',
      title: 'Underwriting',
      icon: 'pen'
    },
    {
      route: 'tools',
      src: 'https://portal.ourbranch.com/ebd/2e8113b696aa80d1beeee1be4e42cf32',
      title: 'Tools & Resources',
      icon: 'tool'
    },
    {
      route: 'training',
      src: 'https://portal.ourbranch.com/ebd/2e8113b696aa8058abbfc05f3a9f5db2',
      title: 'Training',
      icon: 'puzzlePiece'
    },
    {
      route: 'claims',
      src: 'https://portal.ourbranch.com/ebd/2e8113b696aa80a18942cd6653d829c5',
      title: 'Claims',
      icon: 'claim'
    },
    {
      route: 'contact-us',
      src: 'https://portal.ourbranch.com/ebd/2e8113b696aa8005aaa0f7b9276fd729',
      title: 'Contact',
      icon: 'contact'
    }
  ]
};

// Farm Home (Z) and Farm Rental (Y) are now live in `forreston-production` (launched per issue
// #31043). All three masks (F/Z/Y) ship in every environment; no env gating remains. Mirrors
// @ourbranch/brand-config-be/forreston.ts — keep in sync.
export default config;
